import {
  type EBIcon,
  type IconConfig,
  IconConfigVariant,
  type IconStateConfig,
} from '@repo/sdk/models/components';

import { useActiveState } from '@/readings-settings/conditional-state/use-active-state';
import { useGetRefValue } from '@/readings-settings/use-get-ref-value';

const buildDefaultIconConfig = (fallbackIconName: string): IconConfig => ({
  icon: fallbackIconName,
  isPulsing: false,
  variant: IconConfigVariant.Default,
});

const useActiveIconStateConfig = (
  activeIconState: IconStateConfig | undefined,
  defaultIconConfig: IconConfig
): IconConfig | undefined => {
  const tooltipRef = activeIconState?.config?.tooltipContent?.ref;
  const tooltipRefValue = useGetRefValue(
    activeIconState?.config?.tooltipContent?.value ? undefined : tooltipRef
  );
  const iconRefMapRef = activeIconState?.iconRefMap?.ref;
  const iconRefMapValue = useGetRefValue(iconRefMapRef);

  if (!activeIconState) {
    return undefined;
  }

  if (activeIconState.config) {
    const config = { ...activeIconState.config };
    if (config.tooltipContent?.ref && !config.tooltipContent.value && tooltipRefValue != null) {
      config.tooltipContent = {
        ...config.tooltipContent,
        value: tooltipRefValue.toString(),
      };
    }
    return config;
  }

  if (activeIconState.iconRefMap?.ref && iconRefMapValue != null) {
    const iconConfig =
      activeIconState.iconRefMap.iconMap?.[iconRefMapValue.toString()] ?? defaultIconConfig;
    return {
      ...iconConfig,
      tooltipContent: {
        value: iconRefMapValue.toString(),
      },
    };
  }

  return undefined;
};

/**
 * Reactively resolves device icon config from conditional icon states.
 * Prefer this in React components over {@link getDeviceIconConfig}, which reads
 * stream state imperatively and does not re-render when refs hydrate.
 */
export const useDeviceIconConfig = (
  deviceIcon: EBIcon | undefined,
  fallbackIconName = 'Settings',
  allowEmpty = false
): IconConfig | undefined => {
  const defaultIconConfig = buildDefaultIconConfig(fallbackIconName);
  const activeIconState = useActiveState<IconStateConfig>({
    states: deviceIcon?.states ?? [],
  });
  const resolvedFromActiveState = useActiveIconStateConfig(activeIconState, defaultIconConfig);

  if (!deviceIcon) {
    return allowEmpty ? undefined : defaultIconConfig;
  }

  if (!activeIconState) {
    return allowEmpty ? undefined : defaultIconConfig;
  }

  if (resolvedFromActiveState) {
    return resolvedFromActiveState;
  }

  return allowEmpty ? undefined : defaultIconConfig;
};
